import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "montessori-hei-vertailu";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori vs. HEI Schools — Eroja ja yhtäläisyyksiä vanhemmalle";
const DESC = "Montessori ja HEI Schools kunnioittavat molemmat lapsen itsenäisyyttä, mutta painotukset erkanevat. Vertailu pedagogiikasta, ympäristöstä ja arkeen soveltamisesta.";
const PUBLISHED = "2026-07-15";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-hei-vertailu")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Vertailu"
      title={TITLE}
      lead="Montessori ja HEI Schools ovat kaksi suosituinta lapsikeskeistä pedagogiikaa, joista suomalaiset vanhemmat puhuvat. Molemmat kunnioittavat lasta ja hänen oppimistaan, mutta niiden tausta, päivärytmi ja ympäristö poikkeavat toisistaan. Tämä vertailu auttaa valitsemaan perheelle sopivan suunnan."
      readingMinutes={8}
      publishedISO={PUBLISHED}
    >
      <h2>Yhteinen pohja — lapsi on aktiivinen toimija</h2>
      <p>Montessori ja HEI Schools jakavat useita perusarvoja:</p>
      <ul>
        <li><strong>Lapsen kunnioittaminen</strong> — molemmat näkevät lapsen omanlaisenaan persoonana, ei vain aikuisten ohjattavana.</li>
        <li><strong>Oppimisen ilo</strong> — innostus ja uteliaisuus ovat tärkeämpiä kuin pakkosuorittaminen.</li>
        <li><strong>Yksilöllinen tahti</strong> — lapsi etenee omassa tahdissaan, ei ryhmän mukana.</li>
        <li><strong>Aikuinen ohjaajana</strong> — opettajan rooli on havainnoida, valmistella ympäristö ja tukea, ei määrätä.</li>
      </ul>
      <p>Näistä yhtäläisyyksistä huolimatta pedagogiikat eroavat käytännön toteutuksessa merkittävästi.</p>

      <h2>Montessori — strukturoitu ympäristö ja itsenäinen työskentely</h2>
      <p>Montessori-pedagogiikassa ympäristö on tarkkaan suunniteltu. Jokaisella välineellä on oma paikkansa, materiaalit ovat usein pedagogisesti valmistettuja ja lapsi työskentelee pitkiä aikoja yksin tai pienryhmissä.</p>
      <p>Keskeisiä piirteitä:</p>
      <ul>
        <li><strong>Valmiiksi järjestetty ympäristö</strong> — kaikella on paikkansa, ja ympäristö houkuttelee keskittymään.</li>
        <li><strong>Tarkasti suunnitellut materiaalit</strong> — välineet opettavat yhden asian kerrallaan, esimerkiksi hienomotoriikkaa tai aistihavaintoja.</li>
        <li><strong>Pitkä keskittymisjakso</strong> — lapsi saa työskennellä rauhassa niin kauan kuin kiinnostus kestää.</li>
        <li><strong>Montessori-materiaalit</strong> — erityisesti 3–6-vuotiailla käytetään tunnistettavia välineitä, kuten pinkkiä tornia tai pitkää tikkuaskelmaa.</li>
      </ul>

      <h2>HEI Schools — leikki, taide ja yhteisöllisyys</h2>
      <p>HEI Schools perustuu suomalaiseen varhaiskasvatukseen, jossa leikki on oppimisen päämuoto. Päivärytmi on joustavampi, ja painotus on enemmän yhteisöllisessä tekemisessä, taiteessa, liikunnassa ja ulkoilussa.</p>
      <p>Keskeisiä piirteitä:</p>
      <ul>
        <li><strong>Leikki keskiössä</strong> — vapaa leikki on tärkeä oppimismuoto, ei vain tauko.</li>
        <li><strong>Taide ja ilmaisu</strong> — musiikki, kuvataide ja draama ovat vahvasti läsnä.</li>
        <li><strong>Ulkoilu ja luonto</strong> — päivittäinen ulkoilu on olennainen osa rytmiä.</li>
        <li><strong>Yhteisöllisyys</strong> — ryhmätoiminta, yhdessä tekeminen ja lasten välinen vuorovaikutus korostuvat.</li>
      </ul>

      <h2>Suora vertailu</h2>
      <table>
        <thead>
          <tr>
            <th>Ominaisuus</th>
            <th>Montessori</th>
            <th>HEI Schools</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Juuret</strong></td>
            <td>Italialainen Maria Montessori, 1900-luvun alku</td>
            <td>Suomalainen varhaiskasvatus, Helsinki 2010-luku</td>
          </tr>
          <tr>
            <td><strong>Ympäristö</strong></td>
            <td>Strukturoitu, valmiiksi järjestetty, materiaalikeskeinen</td>
            <td>Joustava, leikkiin kannustava, taide- ja luontolähtöinen</td>
          </tr>
          <tr>
            <td><strong>Oppimisen muoto</strong></td>
            <td>Itsenäinen työskentely, toisto, aistimateriaalit</td>
            <td>Leikki, projektit, yhteisöllinen tekeminen</td>
          </tr>
          <tr>
            <td><strong>Aikuisen rooli</strong></td>
            <td>Ohjaaja, joka valmistelee ympäristön ja havainnoi</td>
            <td>Kumppani, joka leikkii, keskustelee ja dokumentoi</td>
          </tr>
          <tr>
            <td><strong>Rytmi</strong></td>
            <td>Pitkät keskittymisjaksot, vapaata valintaa rajatussa ympäristössä</td>
            <td>Joustava päivärytmi, ulkoilu, ryhmähetket</td>
          </tr>
          <tr>
            <td><strong>Kotisovellus</strong></td>
            <td>Matalat kalusteet, avoimet hyllyt, oppimistorni, Pikler-kolmio</td>
            <td>Leikkiin tilaa, ulkoilua, taidehetkiä, yhteisiä rutiineja</td>
          </tr>
        </tbody>
      </table>

      <h2>Kumpi sopii minun perheelleni?</h2>
      <p>Valinta riippuu perheen arvoista ja lapsen luonteesta:</p>
      <ul>
        <li><strong>Montessori voi sopia, jos</strong> lapsi viihtyy pitkiä aikoja yksin keskittyen, pitää selkeästä järjestyksestä ja nauttii toistosta.</li>
        <li><strong>HEI voi sopia, jos</strong> lapsi on sosiaalinen, nauttii leikistä ja liikkumisesta ja kukoistaa joustavassa ryhmässä.</li>
      </ul>
      <p>Useimmat perheet eivät valitse joko-tai. Moni ottaa molemmista parhaat palat: kodissa montessori-henkinen valmiiksi järjestetty tila ja päiväkodissa leikki- ja yhteisöpainotteinen toiminta.</p>

      <h2>Miten yhdistää molempia kotiin?</h2>
      <p>Voit rakentaa lapsellesi ympäristön, joka tukee sekä itsenäisyyttä että leikkiä:</p>
      <ol>
        <li><strong>Luo lapsen mittakaavainen tila</strong> — matala pöytä, tuoli ja avoimet hyllyt.</li>
        <li><strong>Jätä tilaa vapaalle leikille</strong> — älä täytä jokaista hetkeä ohjatulla aktiviteetilla.</li>
        <li><strong>Tarjoa aitoja välineitä</strong> — oppimistorni, Pikler-kolmio, IGLU-palikat ja keittiövälineet.</li>
        <li><strong>Ulkoile päivittäin</strong> — säällä kuin säällä.</li>
        <li><strong>Anna aikaa keskittyä</strong> — älä keskeytä, kun lapsi on uppoutunut.</li>
      </ol>

      <h2>Yhteenveto</h2>
      <p>Montessori ja HEI Schools eivät ole toistensa vastakohtia. Ne ovat kaksi eri painotusta samassa lapsikeskeisessä maailmassa. Montessori antaa työkaluja itsenäiseen oppimiseen ja strukturoituun ympäristöön, HEI Schools puolestaan muistuttaa leikin, taiteen ja ulkoilun voimasta.</p>
      <p>Parasta lapsellesi on usein sekoitus molempia: rauhallinen, valmiiksi järjestetty koti ja iloinen, liikkuva arki. <Link to="/kauppa">Tutustu kalusteisiin, jotka tukevat molempia →</Link></p>
    </BlogPost>
  );
}
